'use client';
import { motion, Variants } from 'motion/react';

// Variants for split-text animation
const splitTextVariants = (delay?: number): Variants => ({
  visible: {
    transition: {
      delayChildren: delay ?? 0,
      staggerChildren: 0.025,
    },
  },
});

// Variants for individual characters (with color animation)
const splitCharVariant = (inverted: ('dark' | 'light') | null = 'dark') => ({
  hidden: {
    color:
      inverted === 'dark' ? 'var(--color-grey-400)' : 'var(--color-grey-400)',
  },
  visible: {
    color:
      inverted === 'dark'
        ? ['var(--color-grey-400)', 'var(--color-indigo-800)']
        : ['var(--color-grey-400)', 'var(--color-white)'],
    transition: {
      color: {
        duration: 0.05,
        ease: [0.625, 0.05, 0, 1] as [number, number, number, number],
        times: [0, 1],
      },
    },
  },
});

export const splitText = ({
  alignment,
  delay,
  inverted,
  onComplete,
  text,
}: {
  alignment?: 'left' | 'center' | 'right' | undefined | null;
  delay?: number;
  inverted?: ('dark' | 'light') | null;
  onComplete?: () => void;
  text: string;
}) => (
  <motion.span
    initial='hidden'
    whileInView='visible'
    viewport={{ once: true }}
    variants={splitTextVariants(delay)}
    style={{ willChange: 'opacity' }}
    onAnimationComplete={onComplete}
    aria-hidden='true'
  >
    <span className='sr-only'>{text}</span>
    {text.split('\n').map((line, index) => (
      <span
        key={index}
        style={{
          display: 'flex',
          justifyContent: alignment || 'left',
          gap: '0 0.33ch',
          flexWrap: 'wrap',
          minHeight: '1.3em',
        }}
      >
        {line.split(' ').map((word, index) => (
          <motion.span key={index} style={{ display: 'inline-flex' }}>
            {word.split('').map((char, index) => (
              <motion.span
                key={index}
                variants={splitCharVariant(inverted)}
                style={{ willChange: 'color, opacity' }}
              >
                {char}
              </motion.span>
            ))}
          </motion.span>
        ))}
      </span>
    ))}
  </motion.span>
);
